'use client';
import React from 'react';
import {
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
} from 'recharts';
import { getScoreBand } from '@/lib/scoring';

interface ProfileRadarProps {
  engine: number;
  ignition: number;
  bounce: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const band = getScoreBand(payload[0]?.value ?? 0);
  return (
    <div
      className="rounded-lg p-3 shadow-xl"
      style={{ background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(225,6,0,0.2)' }}
    >
      <p className="text-xs text-zinc-500 mb-1">{payload[0]?.payload?.category}</p>
      <p className="font-mono font-bold text-lg" style={{ color: band.color }}>{payload[0]?.value}</p>
      <p className="text-xs" style={{ color: band.color }}>{band.label}</p>
    </div>
  );
};

export default function ProfileRadar({ engine, ignition, bounce }: ProfileRadarProps) {
  const data = [
    { category: 'Engine', score: engine, fullMark: 100 },
    { category: 'Ignition', score: ignition, fullMark: 100 },
    { category: 'Bounce', score: bounce, fullMark: 100 },
  ];

  return (
    <div className="card p-5 h-full flex flex-col">
      <h3 className="section-header mb-1">Power Profile Shape</h3>
      <p className="text-xs text-zinc-500 mb-4">Category balance visualization</p>

      {/* Band reference */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {data.map(d => {
          const band = getScoreBand(d.score);
          return (
            <div key={`band-${d.category}`} className="text-center p-2 rounded-lg" style={{ backgroundColor: band.bgColor, border: `1px solid ${band.borderColor}` }}>
              <p className="text-xs text-zinc-500 mb-0.5">{d.category}</p>
              <p className="font-mono font-bold text-lg" style={{ color: band.color }}>{d.score}</p>
              <p className="text-xs" style={{ color: band.color }}>{band.label}</p>
            </div>
          );
        })}
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <RadarChart data={data} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
          <PolarGrid stroke="rgba(255,255,255,0.06)" />
          <PolarAngleAxis dataKey="category" tick={{ fontSize: 12, fill: '#71717a' }} />
          <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <Radar
            name="Score"
            dataKey="score"
            stroke="#E10600"
            fill="#E10600"
            fillOpacity={0.12}
            strokeWidth={2}
            dot={{ fill: '#E10600', r: 5, strokeWidth: 0 }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}